import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
  Signal,
  ViewContainerRef,
} from '@angular/core';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { EventWidget } from '@models/events/event-widget.enum';
import { Event } from '@models/events/event.model';
import { EventRsvpConfirmationPage } from '@models/events/event-rsvp-confirmation-page.model';
import { User } from '@models/users/user.model';
import { UserProfileInfo } from '@models/users/dto/user-profile-info.response';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { EventInfoComponent } from '@modules/events/components/event-info/event-info.component';
import { GenericTicketInfoSuccessComponent } from '@modules/events/components/generic-ticket-info-success/generic-ticket-info-success.component';
import { GetBoughtTicketsDialog } from '@modules/events/dialogs/get-bought-tickets/get-bought-tickets.dialog';
import { AddToCalendarDialog } from '@modules/shared/dialogs/add-to-calendar/add-to-calendar.dialog';
import { LogoCustomFillComponent } from '@modules/shared/logo-custom-fill/logo-custom-fill.component';
import { EventTemplateService } from '@services/events/event-template.service';
import { Router } from '@angular/router';
import { EventService } from '@services/events/event.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { UserService } from '@services/auth/user.service';
import { NotificationService } from '@services/shared/notification.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventRSVPConfirmationPageType } from '@models/events/event-rsvp-confirmation-page.type.enum';
import { EventType } from '@models/events/event-type.enum';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { AppRoutes } from '../../../../routes';
import { RsvpQrCodeDialog } from '@modules/events/dialogs/rsvp-qr-code/rsvp-qr-code.dialog';
import { finalize } from 'rxjs';
import { EventTemplateFont } from '@models/design-templates/event-template-font.model';
import { EventTemplateVisual } from '@models/design-templates/event-template-visual.model';
import { RSVPOptionType } from '@models/event-attendance/rsvp-option-type.enum';
import { EventRSVPConfirmationPageSectionType } from '@models/events/event-rsvp-confirmation-page-section-type.enum';
import { SafeHtmlPipe } from '../../../../pipes/safe-html.pipe';
import { GenericAfterAttendSectionCardComponent } from '@modules/events/dialogs/generic-after-attend-event/components/generic-after-attend-section-card/generic-after-attend-section-card.component';
import { ShareButtonsComponent } from '@modules/shared/components/share-buttons/share-buttons.component';
import { EventTemplateLayout } from '@models/design-templates/event-template-layout.enum';

export enum AfterAttendeeSectionType {
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  PARTICIPANTS = 'PARTICIPANTS',
  SIGN_UP = 'SIGN_UP',
}
@Component({
  selector: 'app-generic-after-attend-event',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SafeHtmlPipe,
    GenericAfterAttendSectionCardComponent,
    ShareButtonsComponent,
    EventInfoComponent,
    LogoCustomFillComponent,
    GenericTicketInfoSuccessComponent,
  ],
  templateUrl: './generic-after-attend-event.dialog.html',
  styleUrl: './generic-after-attend-event.dialog.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    EventTemplateService,
    EventService,
    UserService,
    NotificationService,
    ScreenWidthService,
  ],
})
export class GenericAfterAttendEventDialog implements OnInit {
  event?: Event;
  rsvpType: RSVPOptionType = RSVPOptionType.YES;
  onWaitlist: boolean = false;
  onPendingList: boolean = false;
  eventRsvpConfirmationPage?: EventRsvpConfirmationPage;
  loggedUser?: User;
  profileInfo?: UserProfileInfo;
  actionInProgress?: 'addToWallet';
  eventAttendee?: EventAttendee;
  isDesktop: boolean = true;
  cards = [
    AfterAttendeeSectionType.PARTICIPANTS,
    AfterAttendeeSectionType.SOCIAL_MEDIA,
  ];
  withTickets?: boolean = false;
  ticketOrder?: EventAttendeeTicketOrder;
  myOrders?: EventAttendeeTicketOrder[];

  encodedMainImage: Signal<string> = computed(() => {
    if (
      this.event &&
      (this.event.isGeneric() ||
        (this.event.type === 'WEDDINGPAGE' &&
          this.event.eventTemplate?.layout ===
            EventTemplateLayout.ONE_COLUMN_MODERN)) &&
      this.event.eventTemplate?.seo?.sharingPictureUrl
    ) {
      return this.event.eventTemplate?.seo?.sharingPictureUrl;
    }
    return this.event ? this.event.getEncodedMainImage() : '';
  });

  constructor(
    public dialogRef: MatDialogRef<GenericAfterAttendEventDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private router: Router,
    private eventService: EventService,
    private translate: TranslateService,
    private authService: AuthService,
    private userService: UserService,
    private notificationService: NotificationService,
    private viewContainerRef: ViewContainerRef,
    private screenWidthService: ScreenWidthService,
  ) {
    if (data?.event) {
      this.rsvpType = data.rsvpType;
      this.event = data.event;
      this.eventAttendee = data.eventAttendee;
      this.onWaitlist = data.onWaitlist;
      this.onPendingList = data.onPendingList;
      this.withTickets = data.withTickets;
      this.ticketOrder = data.ticketOrder;

      this.authService.userSubject
        .pipe(takeUntilDestroyed())
        .subscribe((user) => {
          this.loggedUser = user;
          if (user.id) {
            this.userService
              .getUserProfileInfo(user.id)
              .subscribe((profileInfo) => {
                this.profileInfo = profileInfo;
              });
          }
        });

      this.screenWidthService
        .isDesktop()
        .pipe(takeUntilDestroyed())
        .subscribe((isDesktop) => (this.isDesktop = isDesktop));
    }
  }

  ngOnInit(): void {
    if (!this.event) return;
    this.getBoughtTickets();

    // if (this.event.eventTemplate?.rsvpSettings?.rsvpWithoutAccount) {
    //   this.cards = [
    //     AfterAttendeeSectionType.PARTICIPANTS,
    //     AfterAttendeeSectionType.SIGN_UP,
    //     AfterAttendeeSectionType.SOCIAL_MEDIA,
    //   ];
    // }

    let pageType: EventRSVPConfirmationPageType;

    switch (this.afterScreenRsvpType) {
      case RSVPOptionType.NO:
        pageType = EventRSVPConfirmationPageType.NO;
        break;
      case RSVPOptionType.WAITLIST:
        pageType = EventRSVPConfirmationPageType.WAITLIST;
        break;
      case RSVPOptionType.MAYBE:
        pageType = EventRSVPConfirmationPageType.MAYBE;
        break;
      case RSVPOptionType.PENDING_APPROVAL:
        pageType = EventRSVPConfirmationPageType.PENDING;
        break;
      default:
        pageType = EventRSVPConfirmationPageType.YES;
        break;
    }

    if (this.event) {
      this.eventService
        .getRSVPConfirmationPageByType(this.event.id, pageType)
        .subscribe({
          next: (res) => {
            this.eventRsvpConfirmationPage = res;
          },
        });
    }
  }
  getBoughtTickets() {
    if (this.loggedUser && this.event) {
      this.eventService
        .getTicketOrders(this.event.id, true)
        .subscribe((ticketOrders) => {
          if (ticketOrders && ticketOrders.length > 0) {
            this.myOrders = ticketOrders;
          }
        });
    }
  }
  seeTickets(mode: string) {
    let ticket: EventAttendeeTicket | undefined;
    let order: EventAttendeeTicketOrder | undefined = this.ticketOrder;

    if (mode === 'user') {
      ticket = this.ticketOrder?.eventAttendeeTickets.find(
        (x) => x.eventAttendee?.userId === this.loggedUser?.id,
      );
    } else if (mode === 'allOrders') {
      order = undefined;
    }

    this.openGetBoughtTicketsDialog(ticket, order);
  }

  openGetBoughtTicketsDialog(
    ticket?: EventAttendeeTicket,
    order?: EventAttendeeTicketOrder,
  ) {
    const dialogRef = this.dialog.open(GetBoughtTicketsDialog, {
      maxWidth: '456px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        orders: this.myOrders,
        loggedUser: this.loggedUser,
        ticket: ticket,
        order: order,
      },
      panelClass: ['normal-dialog', 'new-normal-dialog'],
      backdropClass: '',
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.refresh) {
        this.getBoughtTickets();
      }
    });
  }

  getTitle(type: EventRSVPConfirmationPageSectionType) {
    const section = this.findSectionByType(type);
    if (!section) return;
    let title;

    if (this.withTickets) {
      return this.translate.instant('APP.EVENT_AFTER_ATTEND.TICKETS.TITLE');
    }
    if (
      section.title === null ||
      section.title === undefined ||
      section.title.trim() === ''
    ) {
      if (type === EventRSVPConfirmationPageSectionType.MAIN) {
        switch (this.eventRsvpConfirmationPage?.type) {
          case EventRSVPConfirmationPageType.NO:
            if (this.event?.type === EventType.INVITATION) {
              title = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.NOT_ATTENDING_HEADER',
              );
            } else {
              title = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.NOT_ATTENDING_HEADER',
              );
            }
            break;
          case EventRSVPConfirmationPageType.WAITLIST:
            if (this.event?.type === EventType.INVITATION) {
              title =
                this.translate.instant('APP.CONGRATS') +
                '\n' +
                this.translate.instant(
                  'APP.EVENT_AFTER_ATTEND.INVITATION.JOIN_WAITLIST_SUCCESS',
                );
            } else {
              title =
                this.translate.instant('APP.CONGRATS') +
                '\n' +
                this.translate.instant(
                  'APP.EVENT_AFTER_ATTEND.JOIN_WAITLIST_SUCCESS',
                );
            }
            break;
          case EventRSVPConfirmationPageType.PENDING:
            if (this.event?.type === EventType.INVITATION) {
              title = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.GET_ON_THE_LIST_SUCCESS',
              );
            } else {
              title = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.GET_ON_THE_LIST_SUCCESS',
              );
            }
            break;
          default:
            if (this.event?.type === EventType.INVITATION) {
              title = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.ATTENDING_HEADER',
              );
            } else {
              if (this.isSportpageEvent) {
                title = 'Nice – du bist erfolgreich angemeldet & dabei!';
              } else {
                title = this.translate.instant('APP.CONGRATS');
              }
            }
            break;
        }
      } else if (type === EventRSVPConfirmationPageSectionType.SOCIAL_MEDIA) {
        title = this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.SECTIONS.SOCIAL_MEDIA_TITLE',
        );
      } else if (type === EventRSVPConfirmationPageSectionType.CREATE_EVENT) {
      }
    } else {
      title = section.title;
    }
    return title;
  }

  get isSportGenericEvent() {
    return true;
    /*   return (
      this.event?.isGeneric() && this.event?.uri.includes('for-every-run-tour')
    );*/
  }

  transformToLargerFont(htmlString: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const paragraphs = doc.querySelectorAll('p');
    paragraphs.forEach((p) => {
      p.classList.add('big-font');
    });
    return doc.body.innerHTML;
  }

  addToCalendar() {
    this.dialog.open(AddToCalendarDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: this.event,
      panelClass: ['normal-dialog', 'event-dialog', 'overlay-theme'],
    });
  }

  getDescription(type: EventRSVPConfirmationPageSectionType) {
    const section = this.findSectionByType(type);
    if (!section) return;
    let description;
    if (this.withTickets) {
      return this.translate.instant(
        'APP.EVENT_AFTER_ATTEND.TICKETS.DESCRIPTION',
        { eventName: this.event?.name },
      );
    }
    if (section.description === null || section.description === undefined) {
      if (type === EventRSVPConfirmationPageSectionType.MAIN) {
        switch (this.eventRsvpConfirmationPage?.type) {
          case EventRSVPConfirmationPageType.NO:
            if (this.event?.type === EventType.INVITATION) {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.NOT_ATTENDING_TITLE',
                { eventName: this.event.name },
              );
            } else {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.NOT_ATTENDING_TITLE',
              );
            }
            break;
          case EventRSVPConfirmationPageType.WAITLIST:
            if (this.event?.type === EventType.INVITATION) {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.JOIN_WAITLIST_SUCCESS_DESCRIPTION',
              );
            } else {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.JOIN_WAITLIST_SUCCESS_DESCRIPTION',
              );
            }
            break;
          case EventRSVPConfirmationPageType.PENDING:
            if (this.event?.type === EventType.INVITATION) {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.GET_ON_THE_LIST_SUCCESS_DESCRIPTION',
                { eventName: this.event.name },
              );
            } else {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.GET_ON_THE_LIST_SUCCESS_DESCRIPTION',
              );
            }
            break;
          default:
            if (this.event?.type === EventType.INVITATION) {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.INVITATION.ATTENDING_TITLE',
                { eventName: this.event.name },
              );
            } else {
              description = this.translate.instant(
                'APP.EVENT_AFTER_ATTEND.TITLE',
              );
            }
            break;
        }
      } else if (type === EventRSVPConfirmationPageSectionType.SOCIAL_MEDIA) {
        description = this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.SECTIONS.SOCIAL_MEDIA_DESCRIPTION',
        );
      } else if (type === EventRSVPConfirmationPageSectionType.CREATE_EVENT) {
        description = this.translate.instant(
          'APP.EVENT_AFTER_ATTEND.CREATE_EVENT_DESC',
        );
      }
    } else {
      description = section.title;
    }
    return description;
  }

  findSectionByType(type: EventRSVPConfirmationPageSectionType) {
    if (type === EventRSVPConfirmationPageSectionType.SOCIAL_MEDIA) {
      const socialMediaSection = this.eventRsvpConfirmationPage?.sections.find(
        (x) => x.type === type && x.visible,
      );
      if (!socialMediaSection) return;
      else {
        if (
          socialMediaSection.socialMediaLinks &&
          socialMediaSection.socialMediaLinks?.length > 0
        ) {
          return socialMediaSection;
        } else return;
      }
    } else {
      return this.eventRsvpConfirmationPage?.sections.find(
        (x) => x.type === type && x.visible,
      );
    }
  }

  getBackgroundUrl(type: EventRSVPConfirmationPageSectionType) {
    const currentSection = this.findSectionByType(type);

    if (currentSection && currentSection.backgroundUrl) {
      return currentSection.backgroundUrl;
    } else {
      return this.afterScreenBackgroundImage;
    }
  }

  close() {
    this.dialogRef.close();
  }

  moreWishlist() {}

  createEvent() {
    this.router.navigate(['/', AppRoutes.Events.createEvent]);
    this.close();
  }

  onTemplateChosen(templateId: number) {
    this.router.navigate(['/', AppRoutes.Events.createEvent], {
      queryParams: { templateId: templateId },
    });
    this.close();
  }

  followCreator() {}

  goToPartypageInsta() {
    window.open('https://www.instagram.com/partypage.ai/', '_blank');
  }

  seeRsvpQRCode() {
    this.dialog.open(RsvpQrCodeDialog, {
      viewContainerRef: this.viewContainerRef,
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      data: {
        event: this.event,
        attendee: this.attendee,
      },
      panelClass: ['normal-dialog', 'secondary-dialog'],
      backdropClass: '',
    });
  }

  addToWallet() {
    if (
      !this.event ||
      !this.attendee?.uuid ||
      this.actionInProgress === 'addToWallet'
    ) {
      return;
    }

    this.actionInProgress = 'addToWallet';

    this.eventService
      .getEventRsvpWalletPass(this.event.id, this.attendee.uuid)
      .pipe(finalize(() => (this.actionInProgress = undefined)))
      .subscribe({
        next: (passPageLink: string) => {
          window.open(passPageLink, '_blank');
        },
        error: () => {
          this.notificationService.error(
            this.translate.instant('APP.EVENT_PAYMENT.COULD_NOT_DOWNLOAD'),
          );
        },
      });
  }

  get attendee(): EventAttendee | undefined {
    return this.event?.getAttendee(this.loggedUser?.id) || this.eventAttendee;
  }

  get isWeddingpageEvent(): boolean {
    return this.event?.type === EventType.WEDDINGPAGE;
  }

  get isInvitationEvent(): boolean {
    return this.event?.type === EventType.INVITATION;
  }

  get isSportpageEvent(): boolean {
    return this.event?.type === EventType.SPORTPAGE;
  }

  get font(): EventTemplateFont | undefined {
    return this.event?.eventTemplate?.font;
  }

  get visual(): EventTemplateVisual | undefined {
    return this.event?.eventTemplate?.visual;
  }

  get afterScreenRsvpType(): RSVPOptionType {
    if (this.rsvpType === RSVPOptionType.NO) {
      return this.rsvpType;
    }

    if (this.onPendingList) {
      return RSVPOptionType.PENDING_APPROVAL;
    } else if (this.onWaitlist) {
      return RSVPOptionType.WAITLIST;
    }

    return this.rsvpType;
  }

  get rsvpOptionAfterScreenTitle(): string | undefined {
    return this.event?.getRsvpConfirmationTextByType(this.afterScreenRsvpType)
      ?.title;
  }

  get afterScreenDescription(): string | undefined {
    return this.event?.getRsvpConfirmationTextByType(this.afterScreenRsvpType)
      ?.description;
  }

  get afterScreenBackgroundImage(): string {
    if (!this.isSportpageEvent) {
      return '';
    }

    if (this.isMizunoEvent) {
      return encodeURI(
        'https://static.eventpage.ai/event-templates/event-templates-1485/after-rsvp-yes-bg.jpg',
      );
    } else if (this.isUaEvent) {
      return encodeURI(
        'https://static.eventpage.ai/event-templates/event-templates-1594/female-running-under-armour.png',
      );
    }

    return '';
  }

  get isMizunoEvent(): boolean {
    return !!this.event?.uri.includes('mizuno');
  }

  get isUaEvent(): boolean {
    return !!this.event?.uri.includes('underarmour-infinite-elite-testival');
  }

  protected readonly RSVPOptionType = RSVPOptionType;
  protected readonly EventRSVPConfirmationPageSectionType =
    EventRSVPConfirmationPageSectionType;
  protected readonly EventWidget = EventWidget;
}
